import angular, { IPromise, IQService } from 'angular';
import { IFontStyleModel } from '../../tn.common/classes';
import {
    BucketsIndex,
    FONT_RIGHT_KEYS,
    FontRightKey,
    IBucket,
    IBucketOption,
    IBucketOptions,
    IBucketSlabs,
    IDjangoBucket,
} from '../../tn.common/classes/Model/Bucket';
import DjangoClient, {
    IPagedResult,
} from '../../tn.django/django-client.service/djangoClient';

const MAX_SAFE_INT = 9007199254740991;

const LICENSE_OPTIONS = [
    'year',
    'cpu',
    'web',
    'web_self_hosted',
    'ebook',
    'app',
] as const;
type LicenseOptionsTuple = typeof LICENSE_OPTIONS;
type LicenseOptions = LicenseOptionsTuple[number];

export function filterEqualOrGreaterBuckets(
    options: IBucketOption[],
    _quantity: number | 'unlimited'
): IBucketOption[] {
    return options.filter(
        (bucket) =>
            (bucket.key != 'free_trial' && bucket.key >= _quantity) ||
            bucket.key === 'unlimited'
    );
}

export function sortBucketLicenseOptionsAscending(
    a: IBucketOption,
    b: IBucketOption
): 1 | -1 | 0 {
    // Make sure buckets are in ascending order with unlimited at the end
    if (a.key === 'unlimited') {
        return 1;
    }
    if (b.key === 'unlimited') {
        return -1;
    }
    if (a.key < b.key) {
        return -1;
    }
    if (a.key > b.key) {
        return 1;
    }
    return 0;
}

export function getEqualOrGreaterBucketOptionValue(
    options: IBucketOption[],
    quantity: number | 'unlimited' | null
): number {
    // If something has no quantity then the price is 0
    if (quantity === 0 || quantity === null) {
        return 0;
    }
    quantity = quantity === -1 ? 'unlimited' : quantity;
    // Otherwise find the matching bucket and return its value
    return filterEqualOrGreaterBuckets(options, quantity).sort(
        sortBucketLicenseOptionsAscending
    )[0].value;
}

export class BucketService {
    protected bucketsPromise: IPromise<BucketsIndex>;

    static $inject = ['$q', 'djangoClient'];
    constructor(protected $q: IQService, protected djangoClient: DjangoClient) {
        this.bucketsPromise = djangoClient
            .getAllPages<IDjangoBucket>('/api/1/bucket/')
            .then((djangoBuckets) => this._transformToClient(djangoBuckets));
    }

    /**
     * tries to parse a string into a number, returns the string if it can't
     */
    protected _numberOrString(string: string): number | string {
        const number = parseInt(string);
        if (isNaN(number)) {
            return string;
        }
        return number;
    }

    protected _transformBucketLicenseToClient(
        bucketEntries: IBucketSlabs
    ): IBucketOption[] {
        if (!bucketEntries) return [];
        const keys = Object.keys(bucketEntries)
            // the !isNaN filter is a workaround for a Safari bug: https://bugs.webkit.org/show_bug.cgi?id=164412
            .filter((key) => !isNaN(bucketEntries[key]))
            // sort the keys with a custom function, otherwise we get 1, 10, 2, 20
            .sort((a, b) => {
                // ensure unlimited is always at the end
                if (a === 'unlimited' && b === 'unlimited') {
                    return 0;
                } else if (a === 'unlimited') {
                    return 1;
                } else if (b === 'unlimited') {
                    return -1;
                }
                // otherwise cast the strings into numbers and sort natually
                return Number(a) - Number(b);
            });

        const bucketArray = keys.map((_key) => {
            const key = this._numberOrString(_key);
            const value = bucketEntries[_key];
            const label =
                key === 'unlimited'
                    ? 'Unlimited'
                    : Number(key).toLocaleString();
            return {
                key,
                label,
                value,
            };
        });
        return bucketArray;
    }

    protected _transformBucketYearToClient(
        bucketEntries: IBucketSlabs
    ): IBucketOption[] {
        const bucketArray = Object.keys(bucketEntries)
            // the !isNaN filter is a workaround for a Safari bug: https://bugs.webkit.org/show_bug.cgi?id=164412
            .filter((key) => !isNaN(bucketEntries[key]))
            .map((_key) => {
                const key = this._numberOrString(_key);
                const value = bucketEntries[_key];
                let label;
                if (_key === '1') {
                    label = '1 year';
                } else if (_key === 'unlimited') {
                    label = 'Perpetual';
                } else {
                    label = _key + ' years';
                }
                return {
                    key,
                    label,
                    value,
                };
            });
        return bucketArray;
    }

    /**
     * Convert bucket data from how Django uses it to the format that we use here.
     * Django: {..., 20000000:75, ...}
     * Angular: [..., {key: 20000000, value: 75, label: '15,000,001-20,000,000'}, ...]
     */
    protected _transformToClient(
        djangoBuckets: IPagedResult<IDjangoBucket>
    ): BucketsIndex {
        const clientBuckets: BucketsIndex = {};

        djangoBuckets.results.forEach((djangoBucket) => {
            const bucket: IBucket = {
                style: djangoBucket.data.style,
                basePrice: djangoBucket.data.basePrice,
                platform: djangoBucket.data.platform,
                year: this._transformBucketYearToClient(djangoBucket.data.year),
                cpu: this._transformBucketLicenseToClient(
                    djangoBucket.data.cpu
                ),
                web: this._transformBucketLicenseToClient(
                    djangoBucket.data.web
                ),
                //eslint-disable-line camelcase
                web_self_hosted: this._transformBucketLicenseToClient(
                    djangoBucket.data.web_self_hosted
                ),
                ebook: this._transformBucketLicenseToClient(
                    djangoBucket.data.ebook
                ),
                app: this._transformBucketLicenseToClient(
                    djangoBucket.data.app
                ),
            };
            clientBuckets[djangoBucket.id] = bucket;
        });

        return clientBuckets;
    }

    /**
     * @param {Object} style - style to get pricing buckets for
     * @returns {Promise} A promise that, when resolved includes the buckets for the style
     */
    public getBuckets(style: IFontStyleModel): IPromise<IBucket> {
        return this.bucketsPromise.then((buckets) => {
            return buckets[style.bucket];
        });
    }

    /**
     * Gets the intersecting license options from the buckets for a list of styles.
     *
     * This is a set intersection operation where only the common values will be in the
     * final result. So if StyleA allows unlimited web licenses, but StyleB does not,
     * it will return a bucket with only non-unlimited licenses.
     *
     * Base prices are ignore, this function should only be used to iterated over
     * to show the available license options in the UI.
     *
     * TODO: Rename to getBucketsLicenseOptionsIntersection()
     *
     * @param {Array<Object>} styles - An array of styles to get a template bucket for
     * @returns {Promise} A promise that when resolved will be a "template bucket"
     */
    public async getIntersectingBuckets(
        styles: IFontStyleModel[]
    ): Promise<IBucketOptions> {
        const buckets = await this.bucketsPromise;
        const bucketIds = styles.map((style) => style.bucket);
        const bucketIdSet = new Set<number>(bucketIds);
        const distinctBucketIds = Array.from(bucketIdSet);

        const clientBuckets: IBucket[] = distinctBucketIds.map(
            (id) => buckets[id]
        );
        const intersection: IBucketOptions = {
            app: [],
            cpu: [],
            ebook: [],
            web: [],
            web_self_hosted: [],
            year: [],
        };

        // iterate over web,app,epub,years ...
        LICENSE_OPTIONS.forEach((licenseOption) => {
            const seenQuantity = Object.create(null);

            // for every bucket we need to evaluate ...
            clientBuckets.forEach((bucket) => {
                // iterate over {key:10000,value:0.5,label:""},{key: 250000,value:1,label:""},...
                bucket[licenseOption].forEach((option) => {
                    const quantity = option.key;
                    // if have already evaluated a particular quantity, exit..
                    if (seenQuantity[quantity]) return;
                    // mark quantity as seen,
                    seenQuantity[quantity] = true;

                    // if the quantity is not in all buckets, it's not an intersection, exit.
                    if (
                        !this._quantityInAll(
                            clientBuckets,
                            licenseOption,
                            quantity
                        )
                    ) {
                        return;
                    }
                    const _option = angular.copy(option);
                    intersection[licenseOption].push(_option);
                });
            });
        });
        return intersection;
    }

    public getClosestAcceptableLicenses(
        intersectingBuckets: IBucketOptions,
        originalLicenses: { fontRights: { key: number | string }[] }[]
    ): unknown[] {
        return originalLicenses.map((license) => {
            const changed = this.getClosestAcceptableFontRights(
                intersectingBuckets,
                license.fontRights,
                false
            );
            if (changed !== license.fontRights) {
                license = angular.copy(license);
                license.fontRights = changed;
            }
            return license;
        });
    }

    public getClosestAcceptableFontRights(
        buckets: IBucketOptions,
        fontRights: { key: number | string }[],
        inPlace = true
    ): { key: number | string }[] {
        let changed = false;

        function _cloneIfNeeded() {
            if (!changed && !inPlace) {
                fontRights = angular.copy(fontRights);
            }
            changed = true;
        }

        FONT_RIGHT_KEYS.forEach((fontRightKey) => {
            const quantityProperty = this._getQuantityProperty(fontRightKey);
            const quantity = fontRights[fontRightKey][quantityProperty];

            // just skip this one; it's empty
            if (quantity === null) {
                return;
            }

            const years = fontRights[fontRightKey].years || null;
            const numericalQuantity = this._getNumericalQuantity(quantity);
            const numericalYears = this._getNumericalQuantity(years);
            let maxDiff;
            const fontRightBucket = this._getOptionsForRight(
                buckets,
                fontRightKey,
                !!fontRights[fontRightKey].self_hosted
            );
            const yearsBucket = buckets.year;

            // font right was not found in the bucket
            if (
                !fontRightBucket.some((fontRight) => fontRight.key === quantity)
            ) {
                maxDiff = MAX_SAFE_INT;
                let targetFontQuantity: number | string | null = null;
                fontRightBucket.forEach((fontRight) => {
                    const diff = Math.abs(
                        numericalQuantity -
                            this._getNumericalQuantity(fontRight.key)
                    );
                    if (diff < maxDiff) {
                        maxDiff = diff;
                        targetFontQuantity = fontRight.key;
                    }
                });

                _cloneIfNeeded();
                fontRights[fontRightKey][quantityProperty] = targetFontQuantity;
            }

            if (years !== null) {
                // years invalid for this bucket
                if (!yearsBucket.some((fontRight) => fontRight.key === years)) {
                    maxDiff = MAX_SAFE_INT;
                    let targetYears: number | string | null = null;
                    yearsBucket.forEach((fontRight) => {
                        const diff = Math.abs(
                            numericalYears -
                                this._getNumericalQuantity(fontRight.key)
                        );
                        if (diff < maxDiff) {
                            maxDiff = diff;
                            targetYears = fontRight.key;
                        }
                    });

                    _cloneIfNeeded();
                    fontRights[fontRightKey].years = targetYears;
                }
            }
        });
        return fontRights;
    }

    public _getNumericalQuantity(value: string | number): number {
        if (value === null || value === undefined) {
            return value;
        }
        if (value.toString().toUpperCase() === 'UNLIMITED') {
            return MAX_SAFE_INT;
        }
        return Number(value);
    }

    protected _getQuantityProperty(fontRightKey: FontRightKey): string | null {
        switch (fontRightKey) {
            case 'app':
                return 'apps';
            case 'desktop':
                return 'computers';
            case 'epub':
                return 'ebooks';
            case 'web':
                return 'views';
        }
        throw new Error('Unrecognized frontRightKey');
    }

    protected _getOptionsForRight(
        buckets: IBucketOptions,
        fontRightKey: FontRightKey,
        selfHosted: boolean
    ): IBucketOption[] {
        switch (fontRightKey) {
            case 'app':
                return buckets.app;
            case 'desktop':
                return buckets.cpu;
            case 'epub':
                return buckets.ebook;
            case 'web':
                return selfHosted ? buckets.web_self_hosted : buckets.web;
        }
        throw new Error('Unrecognized frontRightKey');
    }

    protected _quantityInAll(
        buckets: IBucket[],
        licenseType: LicenseOptions,
        quantity: string | number
    ): boolean {
        return buckets.every((bucket) =>
            bucket[licenseType].some((rights) => rights.key === quantity)
        );
    }
}
